import axios from "axios"
import messErrors from "../constants/errors"
import { notify } from "@/helpers/common"
import i18n from "../i18n"
import { STATUS_CODE, STORAGE_KEYS } from "@/constants/constants"

const baseURL = process.env.VUE_APP_API_ENDPOINT;
const DEFAULT_TIMEOUT = 20000;
const maxCsrfRetries = 3;
let pendingRequests = 0;

// 419 retry attempts are counted PER REQUEST, on the config object — never in
// module state. The CSRF refresh (`csrf-cookie`) goes through serverApi and this
// same instance, so any shared counter would be reset by the refresh's own
// success on every iteration and the retry cap could never be reached.
// mergeConfig() carries unknown keys through mergeDeepProperties, so the count
// survives the re-issued request.
const CSRF_ATTEMPTS_KEY = '__csrfAttempts';

// Internal marker for "CSRF could not be refreshed", kept off user-facing text.
const CSRF_EXHAUSTED_CODE = '20230511001';

// Statuses serverApi handles as control flow, not failures (force-upgrade gate
// and the 2FA prompt). They must not be logged as errors — a successful 2FA
// prompt is a normal outcome, not something a developer needs to see in red.
const CONTROL_FLOW_STATUSES = [
  STATUS_CODE.HTTP_UPGRADE_REQUIRED,
  STATUS_CODE.HTTP_PRECONDITION_REQUIRED,
];

// map to hold in-flight API calls and dedupe by key
const inFlightRequests = new Map();

async function hideLoading() {
  if (!pendingRequests) {
    await store.dispatch("setIsLoading", false);
  }
}

let notifyTimeout;
let notifyMessage;

const instance = axios.create({
  baseURL: baseURL,
  withCredentials: true,
  // Required from axios 0.30+: `withCredentials` no longer implies sending the
  // XSRF header. The API is cross-origin, so isURLSameOrigin() is false and
  // Sanctum would reject every state-changing request with 419 without this.
  withXSRFToken: true,
  headers: {
    "Accept": "application/json",
    "Content-Type": "application/json",
  },
  data: function (params) {
    // Filter out null or empty values
    return Object.keys(params)
      .filter(key => params[key] !== null && params[key] !== '')
      .reduce((obj, key) => {
        obj[key] = params[key]
        return obj
      }, {});
  },
  paramsSerializer: function (params) {
    // Filter out null, undefined or empty values. `undefined` must be included:
    // encodeURIComponent(undefined) yields the literal string "undefined", which
    // the backend would treat as a real filter value.
    return Object.keys(params)
      .filter(key => params[key] !== null && params[key] !== undefined && params[key] !== '')
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(params[key]))
      .join("&")
  },
  transformRequest: [function (data) {
    if (data) {
      // Filter out null or empty values from data
      const filteredData = Object.keys(data).reduce((obj, key) => {
        if (data[key] !== null && data[key] !== "") {
          obj[key] = data[key]
        }
        return obj
      }, {})
      return JSON.stringify(filteredData)
    }
    return data
  }],
  timeout: DEFAULT_TIMEOUT,
});
instance.interceptors.request.use(config => {
  pendingRequests++
  if (config.url === "admin/rental-distributions/create"
    || config.url === "admin/rental-distributions/update"
    || config.url === "admin/rental-distributions/info"
    || config.url === "admin/rental-distributions/details"
    || config.url === "admin/rental-distributions/create-details"
    || config.url === "admin/rental-distributions/start-distributions"
    // Fee Revenue Report: unindexed multi-join scan can be slow on the large transactions table for wide
    // date ranges — allow up to nginx's 300s ceiling (admin-only, on-demand) instead of the 20s default.
    || config.url === "admin/reports/fee-revenue"
    || config.url === "admin/reports/fee-revenue/summary"
  ) {
    config.timeout = 300000
  } else if (config.url === "admin/dashboard/get-statistical-info"
    || config.url === "admin/property-dashboard/get-statistical-info"
    || config.url === "admin/helper/get-tokens-info"
    || config.url === "admin/events"
    || config.url === "contracts/transactions/get-preview"
    || config.url === "contracts/transactions/get-preview-for-existing"
    || config.url === "contracts/transactions/create-for-existing"
    || config.url === "payments"
    || config.url === "payments/trx-users"
    || config.url === "payments/create-payment"
    || config.url === "admin/balance-history/index"
    || config.url === "admin/balance-requests/index"
    || config.url === "admin/balance-requests/bulk"
    || config.url === "admin/referrals/analytics"
    || config.url === "sell-token-requests/approve") {
    config.timeout = 60000
  } else {
    config.timeout = DEFAULT_TIMEOUT
  }
  return config
})
instance.interceptors.response.use(
  function (response) {
    pendingRequests--
    hideLoading()
    return response;
  },
  async function (error) {
    pendingRequests--
    hideLoading()
    if (error && error.response) {
      const status = error.response.status
      if (status === 419) {
        // refresh session token
        const config = error.config || {}
        const attempts = (config[CSRF_ATTEMPTS_KEY] || 0) + 1
        if (attempts <= maxCsrfRetries) {
          try {
            config[CSRF_ATTEMPTS_KEY] = attempts;
            await store.dispatch("refreshSanctumCsrfCookie");
            if (config.data) {
              config.data = JSON.parse(config.data);
            }
            return instance(config);
          } catch (e) {
            // The CSRF refresh itself failed. Log both, but reject with the
            // original 419 — the csrf-cookie endpoint's error would misdescribe
            // what the user actually hit.
            debugLogApiError('csrf-refresh', e, {
              originalStatus: status,
              originalUrl: config.url,
              attempts,
            })
            return Promise.reject(csrfFailureError(error))
          }
        }
        // Retries exhausted: surface a friendly message, keep the marker on the
        // error object and in the log rather than in the toast.
        return Promise.reject(csrfFailureError(error))
      }
      const ex = createError(error)
      return Promise.reject(ex)
    }
    // No response at all: network failure, timeout or CORS. Normalize these too,
    // so they get a friendly message and a debug log instead of surfacing raw
    // AxiosError text ("timeout of 20000ms exceeded") to the user.
    return Promise.reject(createError(error))
  }
);

// Matches the 3-message cap errorHandler.js used to apply when it rendered the
// raw error bag itself.
const MAX_DISPLAYED_MESSAGES = 3

// Collects the string messages out of a Laravel error bag ({ field: ["msg"] }),
// a plain string, or anything in between. Returns [] when nothing string-like is
// found, so callers fall back to a friendly message instead of "[object Object]".
// Entries are trimmed so a body of { errors: { email: [""] } } cannot produce a
// blank-looking toast.
const collectApiMessages = (value) => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? [trimmed] : []
  }
  if (!value || typeof value !== 'object') return []
  return Object.values(value)
    .flatMap(v => (Array.isArray(v) ? v : [v]))
    .filter(v => typeof v === 'string')
    .map(v => v.trim())
    .filter(Boolean)
}

// Pulls the presentable messages out of an API response body, in the order the
// backend uses them: { error: ... }, then Laravel's { errors: ... }, then
// { message: ... }.
const apiMessagesFromPayload = (payload) => {
  if (!payload) return []
  const fromError = collectApiMessages(payload.error)
  if (fromError.length) return fromError
  const fromErrors = collectApiMessages(payload.errors)
  if (fromErrors.length) return fromErrors
  return collectApiMessages(payload.message)
}

// Debug-only logging. Stripped from production builds: vue-cli-service sets
// NODE_ENV=production, so DefinePlugin + terser eliminate the whole branch.
// Deliberately logs the flattened message rather than the raw response body —
// bodies can echo user detail, and the flattened form is what we need anyway.
const debugLogApiError = (label, error, context) => {
  if (process.env.NODE_ENV === 'production') return
  console.error(
      `[serverApi:${label}] ${error?.config?.method || '?'} ${error?.config?.url || '?'} failed:`,
      { ...context, code: error?.code, stack: error?.stack }
  )
}

const createError = (error) => {
  const resp = error?.response
  const payload = resp?.data
  const statusCode = (resp && resp.status) || 500
  const messages = apiMessagesFromPayload(payload)
  // `message` keeps the technical detail for logs and for callers that inspect
  // it — it may be raw axios text.
  const message = messages.join(' ') || error?.message || messErrors.INTERNAL
  const ex = new Error(message)
  ex.source = 'API'
  ex.statusCode = statusCode
  // From axios 0.30+ every HTTP error carries a code (ERR_BAD_REQUEST /
  // ERR_BAD_RESPONSE). Keep it off `data` and expose it for logging instead.
  ex.code = error?.code
  // `data` is what getErrorMessage() and plugins/errorHandler.js render, so it
  // must never carry raw axios text ("Request failed with status code 502") or
  // backend internals. 5xx bodies can contain SQL/stack detail, so they never
  // pass through. Validation bags keep the <br>-separated, 3-message-capped
  // form that errorHandler.js used to build from the raw bag itself.
  ex.data = statusCode >= 500 || !messages.length
      ? messErrors.INTERNAL
      : messages.slice(0, MAX_DISPLAYED_MESSAGES).join('<br>')
  ex.extraData = payload
  if (!CONTROL_FLOW_STATUSES.includes(statusCode)) {
    debugLogApiError('response', error, { status: statusCode, message })
  }
  return ex
}

// A 419 that could not be recovered — either the CSRF refresh failed or the
// retries were exhausted. Rejects with the ORIGINAL 419 (the refresh endpoint's
// error would misdescribe what the user hit) but replaces the raw Laravel text
// ("CSRF token mismatch.") with a friendly message. The support marker stays on
// the error object and in the log instead of the toast.
const csrfFailureError = (error) => {
  const ex = createError(error)
  ex.data = messErrors.INTERNAL
  ex.supportCode = CSRF_EXHAUSTED_CODE
  debugLogApiError('csrf-exhausted', error, {
    status: ex.statusCode,
    supportCode: CSRF_EXHAUSTED_CODE,
  })
  return ex
}

const serverApi = async ({
                           uri,
                           method,
                           data,
                           isLoading = true,
                           isNotify = true,
                           isManualErrorHandling = false,
                         }) => {
  // build a dedupe key
  const key = [
    method,
    uri,
    method === "GET"
        ? JSON.stringify(data || {})
        : JSON.stringify(data || {})
  ].join("|")

  // if identical request is in flight, return its promise
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key)
  }

  // create a wrapped promise that cleans up when done
  const promise = (async () => {
    if (isLoading) {
      await store.dispatch("setIsLoading", true);
    }
    try {
      let params;
      let body;
      if (data != null) {
        if (method === "POST" || method === "PUT") {
          for (const key in data) {
            if (Object.hasOwnProperty.call(data, key)) {
              const element = data[key];
              if (typeof element === "string") {
                data[key] = element.trim();
              }
            }
          }
          body = data;
        } else {
          params = data;
        }
      }
      const response = await instance({
        method: method,
        headers: {
          "Authorization": `Bearer ${localStorage.getItem(STORAGE_KEYS.AUTHORIZATION.key)}`,
          "X-Localization": i18n.global.locale.value,
          "X-Platform": "web"
        },
        data: body,
        url: uri,
        params,
      });
      if (isNotify && response.data && response.data.message) {
        if (!notifyMessage) {
          notifyMessage = response.data.message
          notify({ text: notifyMessage });
          notifyTimeout = setTimeout(() => {
            notifyMessage = null
          }, 2000)
        } else {
          if (notifyMessage !== response.data.message) {
            notify({ text: response.data.message });
          }
        }
      }
      return response.data;
    } catch (e) {
      if (e?.statusCode === STATUS_CODE.HTTP_UPGRADE_REQUIRED) {
        // ignored
      } else if (e?.statusCode === STATUS_CODE.HTTP_PRECONDITION_REQUIRED) {
        await store.dispatch("setRequired2FA", false);
        await store.dispatch("setRequired2FA", true);
      } else {
        if (isManualErrorHandling) {
          throw e
        } else if (e?.source === "API") {
          throw e
        } else {
          const err = new Error(e?.toString() || messErrors.INTERNAL)
          err.source = "API"
          throw err
        }
      }
    } finally {
      if (isLoading) {
        await store.dispatch("setIsLoading", false);
      }
      // remove from in-flight map
      inFlightRequests.delete(key)
    }
  })()

  // store and return
  inFlightRequests.set(key, promise)
  return promise
}

export default serverApi
